/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { useRef, useCallback, useLayoutEffect, useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import useSize from '@hzdg/use-size';
import * as Icons from '@tishman/icons';
import { ThemeUIStyleObject, useThemeUI } from 'theme-ui';

import { Flex, Button, Box, useScrollTo } from '.';

interface TabMenuProps {
  /**
   * TabMenu takes a list of tab objects and displays them in
   * a scrollable list with an active state animation. It takes
   * a callback funtion and passes in the index of the active
   * tab.
   * @callback any
   * @param {number} tab - current active tab index
   * @param {string[]} labels - list of tab objects
   * @param {callback} onTabChange - Callback that takes the
   * current tabs' index.
   * @param {string} tabColor - changes the color of the tab
   * indicator
   * @param {number} tabHeight - changes the height of the tab
   * indicator
   * @param {SxStyleProp} sx - Any overriding styles to change
   * default styling
   * @param {string} className - Some sx props get passed as
   * classNames to components
   */
  tab: number;
  labels: string[];
  onTabChange: (index: number) => void;
  sx?: ThemeUIStyleObject;
  className?: string;
  tabColor?: string;
  tabHeight?: number;
  a11ySectionIdPrefix?: string;
  enableArrows?: boolean;
  hasDiscountedPerformances?: boolean;
}

export const TabMenu = ({
  labels,
  onTabChange,
  tab = 0,
  className,
  sx,
  tabColor = 'text',
  tabHeight = 2,
  a11ySectionIdPrefix = '',
  enableArrows,
  hasDiscountedPerformances = false,
}: TabMenuProps): React.JSX.Element => {
  const themeUI = useThemeUI();
  const scrollport = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const currentFocusedMenuTab = useRef<HTMLButtonElement | null>(null);
  const scrollTo = useScrollTo({ domTarget: scrollport });
  const [{ x, width }, animate] = useSpring(() => ({ x: 0, width: 100 }));
  const measureAndUpdate = useCallback(() => {
    if (container.current) {
      const pageNode = container.current.childNodes[tab] as HTMLElement;
      if (pageNode) {
        void animate.start({
          x: pageNode.offsetLeft,
          width: pageNode.offsetWidth,
        });
        scrollTo(pageNode.offsetLeft);
      }
    }
  }, [tab, animate, scrollTo]);

  useLayoutEffect(() => {
    measureAndUpdate();
  }, [measureAndUpdate, labels]);

  useSize(container, measureAndUpdate);

  const handleTabFocus = (event: React.FocusEvent<HTMLButtonElement>) => {
    currentFocusedMenuTab.current = event.currentTarget;
  };

  //a11y: left and right key handling on tabs for focus shift
  // not related to tab change or normal functionality of the tab selection
  /**
   *  it handles the left and right arrow keys on tabs to shift focus on tabs
   */
  const handleLeftRightKeyboardKey = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    switch (event.key) {
      case 'ArrowLeft':
        (
          currentFocusedMenuTab.current?.previousElementSibling as HTMLElement
        )?.focus();
        break;
      case 'ArrowRight':
        (
          currentFocusedMenuTab.current?.nextElementSibling as HTMLElement
        )?.focus();
        break;
      default:
        break;
    }
  };

  const handleArrowClick = (newTab: number) => () => {
    if (newTab >= 0 && newTab < labels.length) {
      onTabChange(newTab);
    }
  };

  const [isLeftHovered, setIsLeftHovered] = useState(false);
  const [isLeftActive, setIsLeftActive] = useState(false);
  const [isRightHovered, setIsRightHovered] = useState(false);
  const [isRightActive, setIsRightActive] = useState(false);

  return (
    <Box>
      {enableArrows && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Icons.Arrow
            aria-hidden
            onClick={handleArrowClick(tab - 1)}
            onMouseDown={() => setIsLeftActive(true)}
            onMouseOut={() => {
              setIsLeftHovered(false);
              setIsLeftActive(false);
            }}
            onMouseOver={() => setIsLeftHovered(true)}
            onMouseUp={() => setIsLeftActive(false)}
            style={{
              transform: `rotate(180deg) ${
                isLeftHovered ? 'translateX(-3px)' : ''
              } ${isLeftActive ? 'scale(0.8)' : ''}`,
              opacity: tab === 0 ? 0.3 : 1,
              marginRight: themeUI?.theme?.space?.[3],
            }}
          />
          <Icons.Arrow
            aria-hidden
            onClick={handleArrowClick(tab + 1)}
            onMouseDown={() => setIsRightActive(true)}
            onMouseOut={() => {
              setIsRightHovered(false);
              setIsRightActive(false);
            }}
            onMouseOver={() => setIsRightHovered(true)}
            onMouseUp={() => setIsRightActive(false)}
            style={{
              transform: `${isRightHovered ? 'translateX(3px)' : ''} ${
                isRightActive ? 'scale(0.8)' : ''
              }`,
              opacity: tab === labels.length - 1 ? 0.3 : 1,
            }}
          />
        </Box>
      )}
      <Box
        className={className}
        data-module="TabMenu"
        pb={3}
        ref={scrollport}
        sx={{
          'position': 'relative',
          'borderBottom': '1px solid',
          'borderColor': 'text',
          'overflowX': 'scroll',
          'overscrollBehaviorX': 'contain',
          'scrollSnapType': 'x mandatory',
          'scrollbarWidth': 'none',
          '::-webkit-scrollbar': {
            display: 'none',
          },
          ...sx,
        }}
      >
        <Flex
          aria-label="Tab Menu"
          onKeyDown={handleLeftRightKeyboardKey}
          ref={container}
          role="tablist"
          sx={{
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
          }}
        >
          {labels.map((label, index: number) => (
            <Button
              aria-controls={`${a11ySectionIdPrefix}-tabpanel-${index}`}
              aria-label={`${index === tab ? 'Selected' : 'Select'} ${label}`}
              aria-selected={index === tab}
              data-element={`TabItem-${index + 1}`}
              id={`${a11ySectionIdPrefix}-faq-tab-${index}`}
              key={index}
              onClick={() => {
                onTabChange(index);
              }}
              onFocus={(event) => handleTabFocus(event)}
              role="tab"
              sx={{
                'height': '43px',
                'display': 'inline-flex',
                'columnGap': 2,
                'alignItems': 'center',
                'paddingBottom': 0,
                'flexShrink': 0,
                'scrollSnapAlign': 'start',
                'color': index === tab ? 'text' : 'muted',
                'mx': 2,
                ':first-of-type': {
                  ml: 0,
                },
                ':last-of-type': {
                  mr: 0,
                },
                'fontWeight': 'medium',
                'fontSize': ['16px', 'inherit'],
              }}
              tabIndex={index === tab ? 0 : -1}
              variant="tab"
            >
              {hasDiscountedPerformances && label.toUpperCase() === 'EVENING' && (
                <Box
                  sx={{
                    'display': 'inline-flex',
                    'color': 'OLIVE',
                    'stroke': 'black',
                    'svg path': { stroke: 'black' },
                  }}
                >
                  <Icons.DiscountTag />
                </Box>
              )}
              {`${label.charAt(0).toUpperCase()}${label.slice(1)}`}
            </Button>
          ))}
        </Flex>
        <animated.div
          style={{
            x,
            width,
            transformOrigin: 'center left',
            pointerEvents: 'none',
            position: 'absolute',
            bottom: '10px',
            left: 0,
            height: tabHeight,
            backgroundColor:
              (themeUI?.theme?.colors?.[tabColor] as string) ?? 'black',
          }}
        />
      </Box>
    </Box>
  );
};
